import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  MessageSquare,
  MessagesSquare,
  Search,
  Crown,
  Menu,
  ChevronDown,
  User,
  Settings,
  LogOut,
  Sun,
  Moon,
  Users,
  UserCog,
  CalendarCheck2,
  Loader,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { useTheme } from '../hooks/useTheme';
import api, { searchService } from '../services/api';
import { Avatar } from './ui';

const useDebounced = (value, delay = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
};

export default function Header({ onMenu }) {
  const { user, tenant, logout } = useAuth();
  const { waitingCount, requestPermission } = useNotifications();
  const { isDark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isTopPlan = (tenant?.plan || 'starter').toLowerCase() === 'business';

  const [notifOpen, setNotifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const debouncedSearch = useDebounced(searchQ, 250);

  const notifRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!notifOpen && !menuOpen && !searchOpen) return;
    const handler = (e) => {
      if (notifOpen && !notifRef.current?.contains(e.target)) setNotifOpen(false);
      if (menuOpen && !menuRef.current?.contains(e.target)) setMenuOpen(false);
      if (searchOpen && !searchRef.current?.contains(e.target)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [notifOpen, menuOpen, searchOpen]);

  useEffect(() => {
    setNotifOpen(false);
    setMenuOpen(false);
    setSearchOpen(false);
    setSearchQ('');
  }, [location.pathname]);

  // Busca global
  useEffect(() => {
    const q = debouncedSearch.trim();
    if (q.length < 2) {
      setSearchResults(null);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    searchService
      .query(q)
      .then((res) => {
        if (!cancelled) setSearchResults(res.data.data);
      })
      .catch(() => {
        if (!cancelled) setSearchResults({ clientes: [], conversas: [], agendamentos: [], total: 0 });
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const loadWaiting = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tenant/bots');
      const bots = res.data.data.bots || [];
      const lists = await Promise.all(
        bots.map((b) =>
          api
            .get(`/tenant/bots/${b.id}/conversations`)
            .then((r) =>
              (r.data.data.conversations || [])
                .filter((c) => c.status === 'waiting')
                .map((c) => ({ ...c, botId: b.id, botName: b.name })),
            )
            .catch(() => []),
        ),
      );
      const waiting = lists
        .flat()
        .sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));
      setItems(waiting);
    } finally {
      setLoading(false);
    }
  };

  const toggleNotif = async () => {
    const next = !notifOpen;
    setNotifOpen(next);
    setMenuOpen(false);
    setSearchOpen(false);
    if (next) {
      requestPermission().catch(() => {});
      await loadWaiting();
    }
  };

  const accountItem = (Icon, label, onClick, danger = false) => (
    <button
      onClick={() => {
        setMenuOpen(false);
        onClick && onClick();
      }}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13.5px] font-medium transition ${
        danger
          ? 'text-coral-600 hover:bg-coral-50 dark:hover:bg-coral-700/15'
          : 'text-sand-700 dark:text-sand-200 hover:bg-sand-100 dark:hover:bg-sand-800'
      }`}
    >
      <Icon size={17} />
      {label}
    </button>
  );

  const formatTime = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'short',
        })
      : '';

  const formatDateTime = (iso) =>
    iso
      ? new Date(iso).toLocaleString('pt-BR', {
          dateStyle: 'short',
          timeStyle: 'short',
        })
      : '';

  const hasAnyResult =
    (searchResults?.equipe?.length || 0) +
      (searchResults?.clientes?.length || 0) +
      (searchResults?.conversas?.length || 0) +
      (searchResults?.agendamentos?.length || 0) >
    0;

  const ROLE_LABEL_SHORT = {
    owner: 'Proprietário',
    admin: 'Admin',
    viewer: 'Visualizador',
  };

  return (
    <header className="sticky top-0 z-20 h-[64px] bg-sand-100/80 dark:bg-sand-950/80 backdrop-blur-xl border-b border-sand-200 dark:border-sand-800 flex items-center gap-3 px-4 sm:px-7">
      {onMenu && (
        <button
          onClick={onMenu}
          className="lg:hidden w-9 h-9 grid place-items-center rounded-xl text-sand-500 hover:bg-sand-200 dark:hover:bg-sand-800"
          aria-label="Abrir menu"
        >
          <Menu size={20} />
        </button>
      )}

      <div className="hidden sm:flex items-center gap-2 flex-1 max-w-md" ref={searchRef}>
        <div className="relative w-full">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sand-400" />
          <input
            placeholder="Buscar equipe, clientes, conversas, agendamentos…"
            value={searchQ}
            onChange={(e) => {
              setSearchQ(e.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            className="w-full text-[13.5px] pl-10 pr-3 py-2.5 rounded-xl bg-sand-0 dark:bg-sand-900 border border-sand-200 dark:border-sand-800 focus:outline-none focus:ring-2 focus:ring-jade-500/25 text-sand-800 dark:text-sand-100 placeholder:text-sand-400"
          />

          {searchOpen && searchQ.trim().length >= 2 && (
            <div className="absolute left-0 right-0 top-12 z-50 bg-sand-0 dark:bg-sand-900 border border-sand-200 dark:border-sand-800 rounded-2xl shadow-float overflow-hidden animate-fade-in">
              {searchLoading && !searchResults ? (
                <div className="px-4 py-6 text-center text-sand-400">
                  <Loader size={18} className="animate-spin mx-auto" />
                </div>
              ) : !hasAnyResult ? (
                <div className="px-4 py-6 text-center">
                  <Search size={22} className="mx-auto text-sand-300 dark:text-sand-600 mb-2" />
                  <p className="text-sm text-sand-500 dark:text-sand-400">
                    Nenhum resultado para <strong>{searchQ}</strong>
                  </p>
                </div>
              ) : (
                <div className="max-h-[440px] overflow-y-auto divide-y divide-sand-100 dark:divide-sand-800">
                  {searchResults?.equipe?.length > 0 && (
                    <section>
                      <h4 className="px-4 pt-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-wider text-sand-400 flex items-center gap-1.5">
                        <UserCog size={11} /> Equipe
                      </h4>
                      {searchResults.equipe.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => {
                            setSearchOpen(false);
                            setSearchQ('');
                            navigate('/equipe');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-sand-50 dark:hover:bg-sand-800/40 transition"
                        >
                          <Avatar name={m.name || m.email} size={32} />
                          <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-semibold text-sand-900 dark:text-sand-50 truncate">
                              {m.name || m.email}
                            </p>
                            <p className="text-[12px] text-sand-500 dark:text-sand-400 truncate">
                              {m.jobTitle ? `${m.jobTitle} · ` : ''}
                              {ROLE_LABEL_SHORT[m.role] || m.role}
                            </p>
                          </div>
                        </button>
                      ))}
                    </section>
                  )}

                  {searchResults?.clientes?.length > 0 && (
                    <section>
                      <h4 className="px-4 pt-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-wider text-sand-400 flex items-center gap-1.5">
                        <Users size={11} /> Clientes
                      </h4>
                      {searchResults.clientes.map((c) => (
                        <button
                          key={c.phone}
                          onClick={() => {
                            setSearchOpen(false);
                            setSearchQ('');
                            navigate('/clientes');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-sand-50 dark:hover:bg-sand-800/40 transition"
                        >
                          <Avatar name={c.contactName || c.phone} size={32} />
                          <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-semibold text-sand-900 dark:text-sand-50 truncate">
                              {c.contactName || c.phone}
                            </p>
                            <p className="text-[12px] text-sand-400 font-mono truncate">
                              {c.phone}
                            </p>
                          </div>
                          {c.lastSeen && (
                            <span className="text-[11px] text-sand-400 shrink-0">
                              {formatTime(c.lastSeen)}
                            </span>
                          )}
                        </button>
                      ))}
                    </section>
                  )}

                  {searchResults?.conversas?.length > 0 && (
                    <section>
                      <h4 className="px-4 pt-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-wider text-sand-400 flex items-center gap-1.5">
                        <MessagesSquare size={11} /> Conversas
                      </h4>
                      {searchResults.conversas.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => {
                            setSearchOpen(false);
                            setSearchQ('');
                            navigate(`/bots/${c.botId}/conversations`);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-sand-50 dark:hover:bg-sand-800/40 transition"
                        >
                          <Avatar name={c.contactName || c.phone} size={32} tone={1} />
                          <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-semibold text-sand-900 dark:text-sand-50 truncate">
                              {c.contactName || c.phone}
                            </p>
                            <p className="text-[12px] text-sand-500 dark:text-sand-400 truncate">
                              {c.botName} · {c.status}
                            </p>
                          </div>
                          {c.lastMessageAt && (
                            <span className="text-[11px] text-sand-400 shrink-0">
                              {formatTime(c.lastMessageAt)}
                            </span>
                          )}
                        </button>
                      ))}
                    </section>
                  )}

                  {searchResults?.agendamentos?.length > 0 && (
                    <section>
                      <h4 className="px-4 pt-3 pb-1.5 text-[10.5px] font-bold uppercase tracking-wider text-sand-400 flex items-center gap-1.5">
                        <CalendarCheck2 size={11} /> Agendamentos
                      </h4>
                      {searchResults.agendamentos.map((e) => (
                        <button
                          key={e.id}
                          onClick={() => {
                            setSearchOpen(false);
                            setSearchQ('');
                            navigate('/agenda');
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-sand-50 dark:hover:bg-sand-800/40 transition"
                        >
                          <span className="w-8 h-8 rounded-lg bg-jade-50 dark:bg-jade-900/40 grid place-items-center shrink-0">
                            <CalendarCheck2 size={14} className="text-jade-600 dark:text-jade-400" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="text-[13.5px] font-semibold text-sand-900 dark:text-sand-50 truncate">
                              {e.summary}
                            </p>
                            <p className="text-[12px] text-sand-500 dark:text-sand-400 truncate">
                              {formatDateTime(e.start)}
                              {e.customerName && ` · ${e.customerName}`}
                            </p>
                          </div>
                        </button>
                      ))}
                    </section>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 ml-auto">
        <button
          onClick={() => navigate('/planos')}
          className="hidden sm:inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-jade-700 dark:text-jade-300 bg-jade-50 dark:bg-jade-900/40 hover:bg-jade-100 dark:hover:bg-jade-900/60 px-2.5 py-1.5 rounded-full transition"
        >
          <Crown size={14} />
          <span className="capitalize">{tenant?.plan || 'starter'}</span>
          {!isTopPlan && <> · upgrade</>}
        </button>

        <button
          onClick={toggleTheme}
          className="w-9 h-9 grid place-items-center rounded-xl text-sand-500 hover:bg-sand-200 dark:hover:bg-sand-800 transition"
          aria-label="Alternar tema"
          title={isDark ? 'Mudar para modo claro' : 'Mudar para modo escuro'}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <div className="relative" ref={notifRef}>
          <button
            onClick={toggleNotif}
            className="relative w-9 h-9 grid place-items-center rounded-xl text-sand-500 hover:bg-sand-200 dark:hover:bg-sand-800 transition"
            aria-label="Notificações"
          >
            <Bell size={18} />
            {waitingCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-coral-500 ring-2 ring-sand-100 dark:ring-sand-950" />
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-12 z-50 w-[340px] max-w-[92vw] bg-sand-0 dark:bg-sand-900 border border-sand-200 dark:border-sand-800 rounded-2xl shadow-float overflow-hidden animate-fade-in">
              <div className="flex items-center justify-between px-4 py-3 border-b border-sand-200 dark:border-sand-800">
                <p className="font-display font-semibold text-[14px] text-sand-900 dark:text-sand-50">
                  Atendimentos aguardando
                </p>
                <span className="text-[11.5px] text-sand-400">{waitingCount}</span>
              </div>
              <div className="max-h-[360px] overflow-y-auto divide-y divide-sand-100 dark:divide-sand-800">
                {loading ? (
                  <div className="p-6 text-center text-sm text-sand-400">Carregando…</div>
                ) : items.length === 0 ? (
                  <div className="p-8 text-center">
                    <MessageSquare size={24} className="mx-auto mb-2 text-sand-300 dark:text-sand-600" />
                    <p className="text-sm text-sand-500 dark:text-sand-400">
                      Nenhum cliente aguardando.
                    </p>
                  </div>
                ) : (
                  items.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        setNotifOpen(false);
                        navigate(`/bots/${c.botId}/conversations`);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-sand-50 dark:hover:bg-sand-800/60 transition"
                    >
                      <p className="text-[13.5px] font-semibold text-sand-900 dark:text-sand-50 truncate">
                        {c.contact_name || c.phone}
                      </p>
                      <p className="text-[12px] text-sand-500 dark:text-sand-400 truncate">
                        {c.botName}
                      </p>
                      {c.last_message && (
                        <p className="text-[12px] text-sand-400 mt-1 truncate">
                          {c.last_message}
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => {
              setMenuOpen((v) => !v);
              setNotifOpen(false);
              setSearchOpen(false);
            }}
            className="ml-1 flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full hover:bg-sand-200 dark:hover:bg-sand-800 transition"
          >
            <Avatar name={user?.email || 'R'} size={32} />
            <ChevronDown size={15} className="text-sand-400 hidden sm:block" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-12 z-50 w-[240px] bg-sand-0 dark:bg-sand-900 border border-sand-200 dark:border-sand-800 rounded-2xl shadow-float p-1.5 animate-fade-in">
              <div className="flex items-center gap-3 px-2.5 py-2.5 mb-1 border-b border-sand-100 dark:border-sand-800">
                <Avatar name={user?.email || 'R'} size={38} />
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold text-sand-900 dark:text-sand-50 truncate">
                    {user?.email}
                  </p>
                  <p className="text-[12px] text-sand-400 truncate capitalize">
                    {user?.role || 'membro'}
                  </p>
                </div>
              </div>
              {accountItem(User, 'Meu perfil', () => navigate('/perfil'))}
              {accountItem(Settings, 'Configurações', () => navigate('/configuracoes'))}
              {accountItem(
                Crown,
                isTopPlan ? 'Meu plano' : 'Fazer upgrade',
                () => navigate('/planos'),
              )}
              <div className="my-1 border-t border-sand-100 dark:border-sand-800" />
              {accountItem(LogOut, 'Sair da conta', logout, true)}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
